import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useId } from 'react';
import * as yup from 'yup';

import { Alert, FormField, api, parseError, useToast } from 'common';

import { TextFieldControl } from '../../../../../../../components/Control/TextField';

enum ScheduledTriggerAvailability {
  AllowOnlyPublishedFlows = 'AllowOnlyPublishedFlows',
  AllowPublishedAndUnPublishedFlows = 'AllowPublishedAndUnPublishedFlows',
}

interface CreateScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleCreated: () => void;
  flowId: string;
  flowDefinitionId: string;
  organizationId: string;
}

const validationSchema = yup.object({
  name: yup.string(),
  cronExpression: yup.string().required('CRON expression is required'),
  timezone: yup.string().required('Time zone is required'),
  scheduledTriggerAvailability: yup.string().oneOf(Object.values(ScheduledTriggerAvailability)),
});

export function CreateScheduleDialog(props: CreateScheduleDialogProps) {
  const { isOpen, onClose, onScheduleCreated, flowId, flowDefinitionId, organizationId } = props;
  const toast = useToast();

  const scheduledTriggerAvailabilityId = useId();

  const { mutateAsync: createSchedule, isPending } = useMutation({
    mutationFn: (values: api.FlowTriggerSchedules.CreateScheduledFlowTrigger) =>
      api.client.FlowTriggerSchedules.create(values),
    onSuccess: () => {
      onScheduleCreated();
    },
    onError: (error) => {
      toast.showError(parseError(error, 'Failed to create schedule'));
    },
  });

  return (
    <Formik
      initialValues={{
        name: '',
        cronExpression: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        scheduledTriggerAvailability: ScheduledTriggerAvailability.AllowOnlyPublishedFlows,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (values) => {
        await createSchedule({
          flowId,
          flowDefinitionId,
          organizationId,
          name: values.name || 'Scheduled Trigger',
          scheduleSpec: {
            cronExpressions: [values.cronExpression],
            timezone: values.timezone,
          },
          allowUnpublishedFlowRuns:
            values.scheduledTriggerAvailability ===
            ScheduledTriggerAvailability.AllowPublishedAndUnPublishedFlows,
        });
      }}
    >
      {({ isSubmitting, resetForm, values, setFieldValue }) => (
        <Dialog
          open={isOpen}
          onClose={() => {
            onClose();
            resetForm();
          }}
          fullWidth
          maxWidth="sm"
          slotProps={{ paper: { component: Form } }}
        >
          <DialogTitle>Create Scheduled Trigger</DialogTitle>
          <DialogContent>
            <Stack gap={2} sx={{ mt: 1 }}>
              <TextFieldControl name="name" label="Name" placeholder="e.g., Daily Patient Sync" />
              <TextFieldControl
                name="cronExpression"
                label="CRON Expression"
                placeholder="e.g., 0 9 * * 1-5"
                helperText="Standard CRON format: minute hour day-of-month month day-of-week"
                required
              />
              <TextFieldControl
                name="timezone"
                label="Time Zone"
                placeholder="e.g., America/New_York"
                helperText="IANA time zone identifier"
              />
              <FormField id={scheduledTriggerAvailabilityId} label="Availability">
                <RadioGroup
                  id={scheduledTriggerAvailabilityId}
                  value={values.scheduledTriggerAvailability}
                  onChange={(_, value) => {
                    void setFieldValue('scheduledTriggerAvailability', value);
                  }}
                >
                  <FormControlLabel
                    value={ScheduledTriggerAvailability.AllowOnlyPublishedFlows}
                    control={<Radio />}
                    label="Allow only published flows"
                  />
                  <FormControlLabel
                    value={ScheduledTriggerAvailability.AllowPublishedAndUnPublishedFlows}
                    control={<Radio />}
                    label="Allow published and unpublished flows"
                  />
                </RadioGroup>
              </FormField>
              {values.scheduledTriggerAvailability ===
                ScheduledTriggerAvailability.AllowPublishedAndUnPublishedFlows && (
                <Alert variant="warning" title="Running Unpublished Flows">
                  Unpublished flows are mutable and unreviewed. Any changes made to this flow will
                  execute automatically on the next scheduled run. Proceed only if you own this flow
                  and understand the implications.{' '}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" loading={isSubmitting || isPending}>
              Create
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
}
